import React, { useState, useRef, useEffect } from 'react';
import XClose from '../../atoms/Icon/XClose';
import Send from '../../atoms/Icon/Send';
import Upload from '../../atoms/Icon/Upload';
import Tooltip from '../../atoms/Tooltip/Tooltip';

/** Matches portal positioning in AskAsperAccordion. */
export const DISLIKE_FEEDBACK_POPOVER_OUTER_WIDTH = 440;

/** Maximum number of file attachments allowed per feedback submission. */
const MAX_FEEDBACK_FILES = 5;

/** Shown on the upload button (once the limit is reached) and the send button (once exceeded). */
const MAX_FEEDBACK_FILES_TOOLTIP = 'Maximum 5 attachments allowed';

/** Maximum combined size (bytes) of all file attachments per feedback submission. */
const MAX_FEEDBACK_TOTAL_SIZE_BYTES = 10 * 1024 * 1024;

/** Shown on the upload button (once the limit is reached) and the send button (once exceeded). */
const MAX_FEEDBACK_SIZE_TOOLTIP = 'Total attachment size cannot exceed 10MB';

/** Text and optional file attachments from the dislike feedback popover. */
export interface DislikeFeedbackSubmitPayload {
    feedback: string;
    files?: File[];
}

export interface DislikeFeedbackPopoverProps {
    /** Whether the popover is visible */
    open: boolean;
    /** Callback when the popover should close */
    onClose: () => void;
    /** Callback when feedback is submitted (text and/or files) */
    onSubmit: (payload: DislikeFeedbackSubmitPayload) => void;
    /** Shown as: Describe the issue in "<contextTitle>" */
    contextTitle?: string;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

/**
 * DislikeFeedbackPopover - A popover component that appears when user clicks the dislike button
 * Allows users to provide additional feedback about why they disliked the content,
 * with optional unrestricted file attachments (Upload + remove). Host owns upload/API.
 *
 * Design based on Figma: https://www.figma.com/design/tU5vovHzeDFIGuNjQOlBwH/Ask-Asper-3.0?node-id=453-4147
 */
const DislikeFeedbackPopover: React.FC<DislikeFeedbackPopoverProps> = ({
    open,
    onClose,
    onSubmit,
    contextTitle,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const [feedbackText, setFeedbackText] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Focus input when popover opens
    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);

    // Reset text and files when closed
    useEffect(() => {
        if (!open) {
            setFeedbackText('');
            setFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [open]);

    const isOverFileLimit = files.length > MAX_FEEDBACK_FILES;
    const totalFileSize = files.reduce((sum, file) => sum + file.size, 0);
    const isOverSizeLimit = totalFileSize > MAX_FEEDBACK_TOTAL_SIZE_BYTES;
    const canSubmit =
        (feedbackText.trim().length > 0 || files.length > 0) && !isOverFileLimit && !isOverSizeLimit;

    const handleSubmit = () => {
        if (!canSubmit) return;
        onSubmit({
            feedback: feedbackText.trim(),
            files: files.length > 0 ? files : undefined,
        });
        setFeedbackText('');
        setFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    const isUploadDisabled =
        files.length >= MAX_FEEDBACK_FILES || totalFileSize >= MAX_FEEDBACK_TOTAL_SIZE_BYTES;

    const handleUploadClick = () => {
        if (isUploadDisabled) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files;
        if (!selected || selected.length === 0) return;
        setFiles((prev) => [...prev, ...Array.from(selected)]);
        // Allow selecting the same file again after remove
        e.target.value = '';
    };

    const handleRemoveFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    if (!open) return null;

    return (
        <div
            className="bg-white border border-solid border-[#e4e4e7] rounded-xl shadow-[2px_4px_4px_0px_rgba(0,0,0,0.1)] px-6 py-4 flex flex-col gap-4 w-[440px] max-w-[min(440px,calc(100vw-24px))] font-['Open_Sans'] box-border"
            data-test={dataTest}
            data-test-id={dataTestId}
            data-dislike-feedback-popover
        >
            <div className="flex items-start justify-between gap-3 w-full">
                {contextTitle ? (
                    <p
                        className="flex-1 text-[14px] leading-[22px] text-[#18181b] m-0 text-left pr-2"
                        style={{ fontWeight: 400 }}
                    >
                        {`Describe the issue in "${contextTitle}"`}
                    </p>
                ) : (
                    <span className="flex-1" />
                )}
                <button
                    type="button"
                    className="flex items-center justify-center size-[22px] rounded cursor-pointer hover:bg-[rgba(0,0,0,0.04)] bg-transparent border-none transition-colors shrink-0 mt-0.5"
                    onClick={onClose}
                    aria-label="Close feedback"
                    data-test-id={`${dataTestId}-close`}
                >
                    <XClose size={16} color="rgba(0,0,0,0.45)" />
                </button>
            </div>

            <textarea
                ref={inputRef}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tell us more..."
                rows={5}
                className="w-full font-['Open_Sans'] text-[15px] leading-6 text-[#18181b] placeholder-[#a1a1aa] bg-white border border-solid border-[#e4e4e7] rounded-lg outline-none resize-y min-h-[132px] max-h-[220px] px-3 py-3 box-border"
                data-test-id={`${dataTestId}-input`}
            />

            <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
                data-test-id={`${dataTestId}-file-input`}
            />

            {files.length > 0 && (
                <ul
                    className="m-0 p-0 list-none flex flex-col gap-2 w-full"
                    data-test-id={`${dataTestId}-file-list`}
                >
                    {files.map((file, index) => (
                        <li
                            key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                            className="flex items-center justify-between gap-2 rounded-lg border border-solid border-[#e4e4e7] px-3 py-2"
                            data-test-id={`${dataTestId}-file-item-${index}`}
                        >
                            <span
                                className="flex-1 min-w-0 text-[13px] leading-[20px] text-[#3f3f46] truncate"
                                title={file.name}
                            >
                                {file.name}
                            </span>
                            <button
                                type="button"
                                className="shrink-0 flex items-center justify-center size-7 rounded bg-transparent border-none cursor-pointer hover:bg-[rgba(0,0,0,0.04)] transition-colors"
                                onClick={() => handleRemoveFile(index)}
                                aria-label={`Remove ${file.name}`}
                                data-test-id={`${dataTestId}-file-remove-${index}`}
                            >
                                <XClose size={14} color="rgba(0,0,0,0.45)" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <div className="flex items-center justify-end w-full gap-3">
                <Tooltip
                    title={
                        files.length >= MAX_FEEDBACK_FILES
                            ? MAX_FEEDBACK_FILES_TOOLTIP
                            : totalFileSize >= MAX_FEEDBACK_TOTAL_SIZE_BYTES
                              ? MAX_FEEDBACK_SIZE_TOOLTIP
                              : 'Upload upto 5 attachments (pptx/docx/pdf/xlsx/image/text)'
                    }
                    placement="top"
                    getPopupContainer={() => document.body}
                >
                    <button
                        type="button"
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-solid border-[#e4e4e7] bg-white text-[13px] leading-[20px] text-[#18181b] transition-colors ${
                            isUploadDisabled
                                ? 'opacity-[0.56] cursor-not-allowed'
                                : 'cursor-pointer hover:bg-[#fafafa]'
                        }`}
                        style={{ fontWeight: 600 }}
                        onClick={handleUploadClick}
                        disabled={isUploadDisabled}
                        data-test-id={`${dataTestId}-upload`}
                    >
                        <Upload size={16} strokeColor="#18181b" />
                        Upload
                    </button>
                </Tooltip>
                <Tooltip
                    title={
                        isOverFileLimit
                            ? MAX_FEEDBACK_FILES_TOOLTIP
                            : isOverSizeLimit
                              ? MAX_FEEDBACK_SIZE_TOOLTIP
                              : undefined
                    }
                    placement="top"
                    getPopupContainer={() => document.body}
                >
                    <button
                        type="button"
                        className={`flex items-center justify-center size-9 rounded-md p-0 cursor-pointer transition-colors ${
                            canSubmit
                                ? 'bg-[#1f1f1f] hover:bg-[#333333]'
                                : 'bg-[#1f1f1f] opacity-[0.56] cursor-not-allowed'
                        }`}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        aria-label="Submit feedback"
                        data-test-id={`${dataTestId}-submit`}
                    >
                        <Send size={24} color="white" />
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

export default DislikeFeedbackPopover;
